import { z } from "zod";
import { ROUTINE_AREA_IDS, ROUTINE_ROLE_IDS } from "./routineAreas.ts";

/**
 * A connector-catalog category tag.
 *
 * The vocabulary lives in the `connector_categories` table (admin-editable — an
 * admin can add or rename a tag without a deploy, and assignments are validated
 * against it). A connector references a category by its stable `slug`, not the
 * table's numeric pkey: built-ins are hardcoded in code and can only name a slug
 * they write literally, and storing the slug keeps a connector row
 * self-documenting and unaffected by label renames. `label` is the display
 * string shown in the gallery filter.
 */
export const connectorCategory = z.object({
  slug: z.string(),
  label: z.string(),
});

export type ConnectorCategory = z.infer<typeof connectorCategory>;

/**
 * A connector's category-slug assignment as submitted by the admin: bounded and
 * **deduped**, so `["finance", "finance"]` persists as `["finance"]`. Slugs are
 * validated against the live vocabulary separately (handlers reject unknowns).
 */
export const connectorCategorySlugs = z
  .array(z.string())
  .max(20)
  .transform((slugs) => [...new Set(slugs)]);

/**
 * A connector's area as an admin sets it: one built-in area id, the same list
 * routines and skills use, so the Connectors page groups as the Routines page
 * does. Unlike a user's own routine, no custom area: connectors are shared, and
 * a name one admin types would be a group no pack can name. `null` clears it.
 *
 * A string checked against the list, not the enum type, so an admin page built
 * before an area was added can still send that stored id back unchanged.
 */
export const connectorAreaSchema = z
  .string()
  .refine((id) => (ROUTINE_AREA_IDS as readonly string[]).includes(id), "Unknown area")
  .nullable();

/**
 * The roles a connector suits, as for a routine: empty means every role.
 * Deduped, and checked against the list as a string for the reason above.
 */
export const connectorRolesSchema = z
  .array(
    z.string().refine((id) => (ROUTINE_ROLE_IDS as readonly string[]).includes(id), "Unknown role"),
  )
  .max(ROUTINE_ROLE_IDS.length)
  .transform((roles) => [...new Set(roles)]);

/**
 * Seed vocabulary for a fresh database — the categories from the Figma gallery
 * design. The `connector_categories` table is the runtime source of truth; this
 * only bootstraps it (and documents the original set). "All" and "Connected" are
 * gallery meta-filters, not categories, so they aren't here.
 */
export const CONNECTOR_CATEGORY_SEED: readonly ConnectorCategory[] = [
  { slug: "calendar_meetings", label: "Calendar & Meetings" },
  { slug: "email_chat", label: "Email & Chat" },
  { slug: "docs_files", label: "Docs & Files" },
  { slug: "work_projects", label: "Work & Projects" },
  { slug: "finance", label: "Finance" },
  { slug: "people_research", label: "People & Research" },
];
